/**
 * createCollectionGroupStore
 *
 * Singleton Valtio store for a Firestore collection group (read-only).
 * Returns the same instance for the same collection id.
 *
 * Group queries span many parent paths; there is no single write target,
 * so this store exposes only state + connect/disconnect/reconnect.
 *
 * **Lifecycle:** Call `connect()` in major components.
 * **Read:** `useSnapshot(store.state)` for reactive reads.
 *
 * @example
 * ```ts
 * const formVersions = createCollectionGroupStore<FormVersion>('formVersions');
 * formVersions.connect({
 *   queryOptions: { orderBy: { field: 'meta.lastModified', direction: 'desc' }, extraConstraints: [where(...), limit(100)] },
 * });
 *
 * // Read
 * const snap = useSnapshot(formVersions.state);
 * ```
 */

import type { CollectionState, QueryOptions } from '../Store.types';
import { createCollectionState } from '../state/createCollectionState';
import { persistCollectionGroup } from '../persist/persistCollectionGroup';
import { registry } from './registry';

export type CollectionGroupStoreOptions = {
  /** Bypass the registry; required for stores whose query is mutated at runtime. */
  standalone?: boolean;
};

export type CollectionGroupStoreConnectOptions = {
  queryOptions?: QueryOptions;
  onError?: (error: Error) => void;
};

export type CollectionGroupStore<T extends Record<string, unknown>> = {
  readonly type: 'collectionGroup';
  readonly collectionId: string;
  readonly state: CollectionState<T>;
  readonly connected: boolean;
  connect: (options?: CollectionGroupStoreConnectOptions) => Promise<void>;
  disconnect: () => void;
  reconnect: (options?: CollectionGroupStoreConnectOptions) => Promise<void>;
};

export function createCollectionGroupStore<T extends Record<string, unknown>>(
  collectionId: string,
  options?: CollectionGroupStoreOptions,
): CollectionGroupStore<T> {
  const standalone = options?.standalone ?? false;
  const registryKey = standalone ? null : `__group:${collectionId}`;

  if (registryKey !== null) {
    const existing = registry.get(registryKey);
    if (existing) {
      return existing as CollectionGroupStore<T>;
    }
  }

  const state = createCollectionState<T>();

  let unsubscribe: (() => void) | null = null;
  let refCount = 0;
  let epoch = 0;

  const resetState = (): void => {
    state.docs.clear();
    state.docIds.length = 0;
    state.loading = true;
  };

  const store: CollectionGroupStore<T> & { forceDisconnect: () => void } = {
    type: 'collectionGroup' as const,
    collectionId,
    state,
    connected: false,

    async connect(connectOptions?) {
      refCount++;
      if (store.connected) return;
      if (registryKey !== null) registry.set(registryKey, store);
      (store as { connected: boolean }).connected = true;
      if (unsubscribe) return;
      epoch++;
      const myEpoch = epoch;
      const result = persistCollectionGroup<T>(
        collectionId,
        state,
        connectOptions?.onError,
        connectOptions?.queryOptions,
        () => myEpoch === epoch,
      );
      unsubscribe = result.unsubscribe;
    },

    disconnect() {
      refCount = Math.max(0, refCount - 1);
      if (refCount > 0 || !store.connected) return;
      unsubscribe?.();
      unsubscribe = null;
      resetState();
      (store as { connected: boolean }).connected = false;
      if (registryKey !== null) registry.delete(registryKey);
    },

    async reconnect(reconnectOptions?) {
      if (!store.connected) return;
      unsubscribe?.();
      resetState();
      epoch++;
      const myEpoch = epoch;
      const result = persistCollectionGroup<T>(
        collectionId,
        state,
        reconnectOptions?.onError,
        reconnectOptions?.queryOptions,
        () => myEpoch === epoch,
      );
      unsubscribe = result.unsubscribe;
    },

    forceDisconnect() {
      refCount = 0;
      store.disconnect();
    },
  };

  if (registryKey !== null) registry.set(registryKey, store);
  return store;
}
